import { useCallback, useEffect, useState } from 'react';
import { AxiosInstance } from 'axios';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';

import { AccountService } from '../../services/AccountService';
import { useApi } from '../../services/hooks/apiHook';

import { useAuth } from '../../store/auth';
import { userRoleAdapter } from '../../adapters';
import { OFFSHORE_FLOW_SESSION_KEY } from '../../constants/offshoreLpIntent';
import { MID_TRANSFER_PROGRESS_STEPS } from '../../pages/Onboarding/CompanyTransfer/constants';

const accountPlansCache = new Map<string, any>();
const accountPlansPromiseMap = new Map<string, Promise<any>>();

const isMidTransferProgress = (progressStep?: string | null) =>
  !!progressStep && (MID_TRANSFER_PROGRESS_STEPS as readonly string[]).includes(progressStep);

function OnboardingGuard({ element }: { element?: React.ReactNode | React.ComponentType }) {
  const { planSlug, token, setPlanSlug, user, setConfigSlug } = useAuth();

  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const userRole = userRoleAdapter(user!.role_id) || 'customer';
  const api: AxiosInstance = useApi();
  const [isCheckingPlans, setIsCheckingPlans] = useState(true);

  const offshoreFlowRawFromQuery = new URLSearchParams(location.search).get('offshore_flow');
  const sessionOffshoreFlowRaw =
    typeof window !== 'undefined' ? sessionStorage.getItem(OFFSHORE_FLOW_SESSION_KEY) : null;
  const offshoreFlowRawEffective = offshoreFlowRawFromQuery ?? sessionOffshoreFlowRaw;
  const offshoreFlow = offshoreFlowRawEffective?.toLowerCase();
  const skipChooseOnboarding = offshoreFlow === 'open' || offshoreFlow === 'transfer';

  const fetchAccountPlans = useCallback(async () => {
    // Não faz a chamada se estiver na página de escolher onboarding
    if (location.pathname === '/choose-onboarding') {
      return;
    }

    if (userRole !== 'customer' || !token) {
      setIsCheckingPlans(false);
      return;
    }

    const key = `${token ?? ''}|${i18n.language ?? ''}`;

    if (accountPlansCache.has(key)) {
      const cached = accountPlansCache.get(key);
      if (cached) {
        setPlanSlug(cached.account_plan_slug);
        setConfigSlug(cached.configuration_plan_slug);
      }
      setIsCheckingPlans(false);
      return cached;
    }

    if (accountPlansPromiseMap.has(key)) {
      const pending = accountPlansPromiseMap.get(key);
      await pending;
      setIsCheckingPlans(false);
      return pending;
    }

    try {
      setIsCheckingPlans(true);
      const request = AccountService.getAccountPlans(api, {
        headers: { 'Content-Language': i18n.language, Authorization: `Bearer ${token}` }
      })
        .then((data) => {
          if (data) {
            accountPlansCache.set(key, data);
            setPlanSlug(data.account_plan_slug);
            setConfigSlug(data.configuration_plan_slug);
          } else {
            accountPlansCache.set(key, null);
          }
          return data;
        })
        .catch((error: any) => {
          accountPlansCache.delete(key);
          // Se for 404, significa que o usuário ainda não tem plan configurado
          // Isso é esperado para novos usuários, então não redireciona
          if (error?.response?.status === 404) {
            accountPlansCache.set(key, null);
            return null;
          }
          throw error;
        })
        .finally(() => {
          accountPlansPromiseMap.delete(key);
          setIsCheckingPlans(false);
        });

      accountPlansPromiseMap.set(key, request);
      const result = await request;
      
      // Se não há plan configurado, redireciona para escolher onboarding
      // (ou retoma transfer se o progress_step indica wizard em andamento).
      if (!result) {
        if (skipChooseOnboarding) return null;
        if (isMidTransferProgress(user?.account?.progress_step)) {
          navigate('/company-transfer');
          return null;
        }
        navigate('/choose-onboarding');
      }
    } catch (e: any) {
      // Apenas redireciona se não for um erro 404 (que já foi tratado acima)
      if (!skipChooseOnboarding && e?.response?.status !== 404) {
        if (isMidTransferProgress(user?.account?.progress_step)) {
          navigate('/company-transfer');
        } else {
          navigate('/choose-onboarding');
        }
      }
      setIsCheckingPlans(false);
    }
  }, [api, i18n.language, location.pathname, navigate, setConfigSlug, setPlanSlug, token, userRole, skipChooseOnboarding, user?.account?.progress_step]);

  useEffect(() => {
    void fetchAccountPlans();
  }, [fetchAccountPlans]);

  // Se o planSlug já tiver sido definido, não há motivo para manter o "skip" por sessão.
  useEffect(() => {
    if (typeof window !== 'undefined' && planSlug) {
      sessionStorage.removeItem(OFFSHORE_FLOW_SESSION_KEY);
    }
  }, [planSlug]);

  useEffect(() => {
    if (!token) {
      accountPlansPromiseMap.clear();
      accountPlansCache.clear();
    }
  }, [token]);

  if (userRole === 'customer' && isCheckingPlans) {
    return <></>;
  }

  const Element: React.ComponentType = element as React.ComponentType;

  return <>{element && typeof element === 'function' ? <Element /> : element}</>;
}

export { OnboardingGuard };
